import React, { useEffect } from "react";
import Head from "next/head";
import Link from "next/link";
import useSWR from "swr";
import { UrlObject } from "url";
import { endpoints } from "lib/actions/endpoints/endpoints";
import { fetcher } from "lib/actions/fetcher";
import { sort } from "lib/actions/util";
import { processProjectMapMarkers } from "lib/actions/processProjectMapMarkers";
import { useRouter } from "next/router";
import { useProjects } from "./lib/hooks/useProjects";
import { useCesium } from "components/elements/CesiumViewer/core/useCesium";
import { useGeographyMarkers } from "lib/hooks/useGeographyMarkers";
import { useWorkspaces } from "lib/hooks/useWorkspaces";
import { KeyframeGeography } from "lib/types/KeyframeGeography";
import { WorkspaceModel } from "lib/types/workspaces/WorkspaceModel";
import AddSvg from "components/icons/24/actions/action-add.svg";
import InfoSvg from "components/icons/24/notifications/info.svg";
import WorkspaceSvg from "components/icons/WorkspaceIcon";
import EmptyStateProjectsSvg from "components/icons/empty-state-projects.svg";
import PadlockSvg from "components/icons/24/other/lock.svg";
import Scrollable from "components/elements/Scrollable/Scrollable";
import SidePanelSection from "../../elements/SidePanel/SidePanelSection";
import MenuItem from "components/elements/MenuItems/MenuItem";
import GroupedMenuItems from "components/elements/MenuItems/GroupedMenuItems";
import MenuItemArrow from "components/elements/MenuItems/MenuItemArrow";
import SidePanelHeader from "components/elements/SidePanel/SidePanelHeader";
import SidePanelActionBar from "../../elements/SidePanel/SidePanelActionBar";
import SidePanelActionButton from "../../elements/SidePanel/SidePanelActionButton";
import ErrorMessage from "components/elements/Error/ErrorMessage";
import LoadingSkeleton from "components/elements/Loading/LoadingSkeleton";
import ProjectsLoadingSkeleton from "./ProjectsLoadingSkeleton";
import HoverTooltip from "components/elements/Tooltips/HoverTooltip";
import MessageBanner, {
  MessageType,
} from "components/elements/MessageBanner/MessageBanner";

export default function ProjectsPage() {
  const router = useRouter();
  const { workspaces, hasError, isLoading } = useWorkspaces();
  const { cameraPlugin, assetsPlugin, toolManager } = useCesium();
  const { data: geographyData } = useSWR<KeyframeGeography>(
    endpoints.projectsGeography,
    fetcher,
  );

  const geography: KeyframeGeography = processProjectMapMarkers(geographyData);
  useGeographyMarkers(geography?.features, (id) => {
    router.push(createProjectUrl(id));
  });

  const workspacesSorted = sort(workspaces, (w) => w.name, true);
  const hasContent = !hasError && !isLoading && workspaces?.length > 0;

  useEffect(() => {
    assetsPlugin.hideAll();
    toolManager.resetAllTools();
  }, []);

  useEffect(() => {
    if (!geographyData) return;

    cameraPlugin.flyToBounds(geographyData.bbox, true).catch(async () => {
      //If invalid bbox is provided then default to a view of Australia
      const ausBoundingBox = [
        113.338953078, -43.6345972634, 153.569469029, -10.6681857235,
      ];

      await cameraPlugin.flyToBounds(ausBoundingBox, true);
    });
  }, [geographyData, cameraPlugin]);

  return (
    <>
      <Head>
        <title>Projects</title>
      </Head>
      <SidePanelHeader>
        <SidePanelActionBar text="Projects">
          <SidePanelActionButton
            tip="Create"
            href={{
              pathname: "/projects/create",
            }}
          >
            <AddSvg />
          </SidePanelActionButton>
        </SidePanelActionBar>
      </SidePanelHeader>

      <div className="flex h-full flex-col overflow-hidden ">
        <Scrollable>
          <SidePanelSection className="space-y-2 py-4">
            {hasContent &&
              workspacesSorted.map((w) => (
                <WorkspaceSection key={w.id} workspace={w} />
              ))}
            {!hasContent && (
              <div className="text-center">
                {hasError ? (
                  <ErrorMessage>
                    There was an error loading the list of projects
                  </ErrorMessage>
                ) : isLoading ? (
                  <ProjectsLoadingSkeleton />
                ) : (
                  <div className="flex w-full flex-col items-center rounded-md bg-neutral-50 p-5 pb-7 font-light text-disabled">
                    <EmptyStateProjectsSvg />
                    <h3 className="mt-4 font-semibold">No projects</h3>
                    <p className="mt-2 text-sm">
                      Create a project <AddSvg className="inline w-5" /> or
                      check you have been granted access to any existing
                      projects.
                    </p>
                  </div>
                )}
              </div>
            )}
          </SidePanelSection>
        </Scrollable>
      </div>
    </>
  );
}

function WorkspaceSection(p: { workspace: WorkspaceModel }) {
  const router = useRouter();
  const { projects: projectsAll, error, isLoading } = useProjects();

  const projects = sort(
    projectsAll?.filter((project) => project.workspace.id === p.workspace.id),
    (p) => p.name,
    true,
  );
  const isShowingSettings = p.workspace.canManage;
  const hasContent = !error && !isLoading && projects?.length > 0;

  const workspaceSettingsUrl: UrlObject = {
    pathname: `/workspaces/[workspaceId]/settings`,
    query: { workspaceId: p.workspace.id },
  };

  return (
    <div className="space-y-4 rounded-lg border border-neutral-200 bg-white p-3">
      <div className="flex items-center justify-between">
        <div className="flex cursor-default items-center space-x-4">
          {p.workspace.isSubscriptionValid && <WorkspaceSvg />}
          {!p.workspace.isSubscriptionValid && (
            <HoverTooltip
              placement="top-start"
              hoverOptions={{ move: false }}
              customTooltipContent="Workspace is on hold"
            >
              <PadlockSvg className="w-5 text-warning-500" />
            </HoverTooltip>
          )}
          <h1 className="text-sm font-light text-gray-700">
            {p.workspace.name}
          </h1>
        </div>

        {isShowingSettings && (
          <Link
            href={workspaceSettingsUrl}
            className="inline-flex items-center rounded-full border border-gray-200 px-3 py-1 text-xs text-gray-700 transition-colors hover:bg-gray-100"
          >
            <span>Settings</span>
          </Link>
        )}
      </div>

      {!p.workspace.isSubscriptionValid && (
        <div
          onClick={() => router.push(`/workspaces/${p.workspace.id}/paywall`)}
          className="cursor-pointer"
        >
          <MessageBanner messageType={MessageType.Warning} colourWidth={"sm"}>
            <span className="flex justify-between text-sm font-light">
              Workspace on hold
              <InfoSvg className="w-5 text-amber-500" />
            </span>
          </MessageBanner>
        </div>
      )}

      {hasContent && (
        <GroupedMenuItems>
          {projects.map((project) => (
            <div
              key={project.id}
              {...(!p.workspace.isSubscriptionValid && {
                onClick: () =>
                  router.push(`/workspaces/${p.workspace.id}/paywall`),
                className: "cursor-pointer",
              })}
            >
              <MenuItem
                title={project.name}
                {...(p.workspace.isSubscriptionValid && {
                  rightSideSections: [<MenuItemArrow key="arrow" />],
                  href: createProjectUrl(project.id),
                })}
              />
            </div>
          ))}
        </GroupedMenuItems>
      )}
      {!hasContent && (
        <>
          {error ? (
            <ErrorMessage>Error loading workspace projects</ErrorMessage>
          ) : isLoading ? (
            <LoadingSkeleton />
          ) : (
            <div className="mb-1 cursor-default rounded-sm bg-gray-100 p-5 pb-7 font-light text-gray-400">
              <h3>No projects</h3>
              <p className="mt-2 text-sm">
                Create a project <AddSvg className="inline w-3.5" /> or check
                you have been granted access to any existing projects.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function createProjectUrl(id: number | string): UrlObject {
  return {
    pathname: `/projects/[projectId]`,
    query: { projectId: id },
  };
}
